import * as main from '../main.js';
import { dirToRad, degToRad } from '../utils.js';
import { ctx } from '../canvas.js';
import * as player from '../player.js';
import * as scene from '../scene.js';
import { Tween } from '../tweens.js';
import * as loader from '../loader.js';

export {
    init,
    update,
    draw,
    getMode,
    toggleMode,
    config,
    cameraPosition,
    zoomToOrigin,
    zoomToPlayer,
}

// Config
const config = {
    compassRadius: 150, // px
    maxCompassRadius: 150, // px (the max radius of the compass is gonna be)
    pixelsPerMeter: 25, // px
    pointSeparation: 1, // Meters between each point
};

let trackPlayer = true;
const rotateWhileZooming = false;
const cameraPosition = { x: 0, y: 0 };
let cameraDirection = 0;

let mode = 'heading-up'; // 'north-up' or 'heading-up'

let pointsInterval, offscreenCanvas;

let playerColor = 'white';

function init() {
    pointsInterval = config.pointSeparation * config.pixelsPerMeter;
    offscreenCanvas = new OffscreenCanvas(
        (2 * config.maxCompassRadius + pointsInterval * 2) * devicePixelRatio,
        (2 * config.maxCompassRadius + pointsInterval * 2) * devicePixelRatio
    );
    preparePointFieldImage();

    // Example for changing playerColor every 500ms
    // setInterval(() => {
    //     playerColor = `hsl(${Math.random()*360}, 100%, 50%)`;
    // }, 500);
}

function update() {
    if (trackPlayer) {
        cameraPosition.x = player.position.x;
        cameraPosition.y = player.position.y;
        cameraDirection = player.direction;
    }
}

function draw() {
    // Example of a dynamic compass radius
    // config.compassRadius = 100 + Math.sin(Math.floor(Date.now() / 5 % 1000) / 1000 * Math.PI * 2) * 50;

    // Create clip path (same as compass outline)
    ctx.beginPath();
    ctx.arc(main.config.width/2, main.config.height/2, config.compassRadius, 0, Math.PI * 2);

    // Clip further draws to compass outline
    ctx.save();
    ctx.clip();

    drawWorld();

    // Restore pre-clip
    ctx.restore();

    drawCompass();
}

function drawWorld() {
    // Add translate and rotate to the transformation matrix for world coordinates
    ctx.save();
    ctx.translate(main.config.width/2, main.config.height/2);
    if (mode === 'heading-up') ctx.rotate(degToRad(-cameraDirection));

    // Draw point field
    drawPointField();

    // Draw world bounds
    drawWorldBounds();

    // Draw entities
    drawPOIs();
    drawPlayer();

    // Restore transformation matrix to canvas coordinates
    ctx.restore();
}

function drawCompass() {
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1;

    // Draw compass directions
    drawCompassDirection(0, 'N');
    drawCompassDirection(90, 'E');
    drawCompassDirection(180, 'S');
    drawCompassDirection(270, 'O');

    // Draw compass outline
    ctx.beginPath();
    ctx.arc(main.config.width/2, main.config.height/2, config.compassRadius, 0, Math.PI * 2);
    ctx.stroke();
}

function drawWorldBounds() {
    const cameraPos = worldToCanvas(cameraPosition);

    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo((scene.config.worldBounds.left) * config.pixelsPerMeter - cameraPos.x, (scene.config.worldBounds.top) * config.pixelsPerMeter - cameraPos.y);
    ctx.lineTo((scene.config.worldBounds.right) * config.pixelsPerMeter - cameraPos.x, (scene.config.worldBounds.top) * config.pixelsPerMeter - cameraPos.y);
    ctx.lineTo((scene.config.worldBounds.right) * config.pixelsPerMeter - cameraPos.x, (scene.config.worldBounds.bottom) * config.pixelsPerMeter - cameraPos.y);
    ctx.lineTo((scene.config.worldBounds.left) * config.pixelsPerMeter - cameraPos.x, (scene.config.worldBounds.bottom) * config.pixelsPerMeter - cameraPos.y);
    ctx.closePath();
    ctx.stroke();
}

function drawPlayer() {
    const cameraPos = worldToCanvas(cameraPosition);
    const playerPos = worldToCanvas(player.position);

    // Add translate and rotate to the transformation matrix for world coordinates
    // TODO: convert all other draws with initial transforms then local coordinates like this method
    ctx.save();
    ctx.translate(playerPos.x - cameraPos.x, playerPos.y - cameraPos.y);
    ctx.rotate(degToRad(player.direction));

    // Draw player
    ctx.beginPath();
    ctx.arc(0, 12, 8, dirToRad(0) - degToRad(45), dirToRad(0) + degToRad(45));
    ctx.lineTo(0, -8);
    ctx.closePath();

    ctx.fillStyle = playerColor;
    ctx.fill();

    // Restore transformation matrix to canvas coordinates
    ctx.restore();
}

function drawCompassDirection(dir, txt) {
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'white';

    // Add player direction
    const angle = (mode === 'heading-up') ? dir - cameraDirection : dir;

    // Draw direction triangle
    ctx.beginPath();
    ctx.arc(main.config.width/2, main.config.height/2, config.compassRadius, dirToRad(angle) - degToRad(8), dirToRad(angle) + degToRad(8));
    ctx.lineTo(
        main.config.width/2 + Math.sin(degToRad(angle)) * (config.compassRadius + 20),
        main.config.height/2 - Math.cos(degToRad(angle)) * (config.compassRadius + 20)
    );
    ctx.lineTo(
        main.config.width/2 + Math.sin(degToRad(angle) - degToRad(8)) * (config.compassRadius),
        main.config.height/2 - Math.cos(degToRad(angle) - degToRad(8)) * (config.compassRadius)
    );
    // Only North is filled
    if (dir === 0) {
        ctx.fill()
        ctx.fillStyle = 'black';
    } else {
        ctx.stroke();
    }
    // Draw text
    ctx.font = '10px Helvetica Neue';
    ctx.fillText(
        txt,
        main.config.width/2 + Math.sin(degToRad(angle)) * (config.compassRadius + 7) - 3.5,
        main.config.height/2 - Math.cos(degToRad(angle)) * (config.compassRadius + 7) + 3.5
    );
}

function preparePointFieldImage() {
    const offctx = offscreenCanvas.getContext('2d');
    offctx.clearRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);

    offctx.fillStyle = '#444';
    for (let x = 0; x <= offscreenCanvas.width; x += pointsInterval) {
        for (let y = 0; y <= offscreenCanvas.height; y += pointsInterval) {
            offctx.beginPath();
            offctx.arc(x, y, 2, 0, Math.PI * 2);
            offctx.fill();
        }
    }
}

function drawPointField() {
    const cameraPos = worldToCanvas(cameraPosition);
    const offsetX = cameraPos.x % pointsInterval;
    const offsetY = cameraPos.y % pointsInterval;

    ctx.drawImage(
        offscreenCanvas,
        -config.maxCompassRadius - pointsInterval - offsetX,
        -config.maxCompassRadius - pointsInterval - offsetY,
    );
}

function drawPOIs() {
    // TODO: filter out POIs that are outside the compass radius
    const visiblePOIs = scene.POIs.filter(p => !p.invisible);
    for (const p of visiblePOIs) {
        const cameraPos = worldToCanvas(cameraPosition);
        const poiPos = worldToCanvas(p.position);

        ctx.fillStyle = p.color;
        ctx.beginPath();
        if (p.image) {
            // Draw image
            const imageWidth = loader.images[p.image].width / loader.images[p.image].ppm * config.pixelsPerMeter;
            const imageHeight = loader.images[p.image].height / loader.images[p.image].ppm * config.pixelsPerMeter;
            ctx.drawImage(
                loader.images[p.image],
                poiPos.x - cameraPos.x - imageWidth/2,
                poiPos.y - cameraPos.y - imageHeight/2,
                // Convention: images are sized at 25 pixels per meter
                imageWidth,
                imageHeight,
            );
        } else if (p.type === 'triangle') {
            // Draw a triangle centered on p.position
            ctx.save();
            ctx.translate(poiPos.x - cameraPos.x, poiPos.y - cameraPos.y);
            ctx.rotate(degToRad(p.rotation));
            ctx.beginPath();
            ctx.moveTo(Math.cos(degToRad(150)) * p.radius, Math.sin(degToRad(150)) * p.radius);
            ctx.lineTo(0, -p.radius);
            ctx.lineTo(Math.cos(degToRad(30)) * p.radius, Math.sin(degToRad(30)) * p.radius);
            ctx.closePath();
            ctx.restore();
        } else if (p.type === 'circle' || !p.type) {
            // Draw circle (default type)
            ctx.arc(
                (poiPos.x - cameraPos.x),
                (poiPos.y - cameraPos.y),
                p.radius || 5,
                0,
                Math.PI * 2
            );
        }

        // Handling for styled shapes
        if (!p.image) {
            if (!p.style) {
                ctx.fill();
            } else if (p.style === 'idle') {
                ctx.fillStyle = '#223';
                ctx.strokeStyle = '#99b';
                ctx.lineWidth = 1;
                ctx.fill();
                ctx.stroke();
            } else if (p.style === 'active') {
                const timebase = Math.abs(Math.sin(Math.floor(Date.now() / 3 % 1000) / 1000 * Math.PI * 2));
                ctx.strokeStyle = p.color.replace('50%', `${timebase * 40 + 50}%`);
                ctx.lineWidth = 2;
                ctx.fill();
                ctx.stroke();
            } else if (p.style === 'complete') {
                const timebase = Math.floor(Date.now() % 2000) / 2000;
                ctx.fillStyle = `hsl(${timebase * 360}, 50%, 30%)`;
                ctx.strokeStyle = `hsl(${timebase * 360}, 100%, 50%)`;
                ctx.lineWidth = 2;
                ctx.fill();
                ctx.stroke();
            }
        }
    }
}

function getMode() {
    return mode;
}

function toggleMode() {
    mode = (mode === 'heading-up') ? 'north-up' : 'heading-up';
}

function zoomToOrigin() {
    trackPlayer = false;
    new Tween(cameraPosition, { x: 0, y: 0 }, 2, 'easeInOutQuad');
    if (rotateWhileZooming) new Tween(cameraDirection, 0, 2, 'easeInOutQuad', (v) => {
        cameraDirection = v;
    });
}

function zoomToPlayer() {
    new Tween(cameraPosition, player.position, 2, 'easeInOutQuad', null, (tween) => {
        trackPlayer = true;
        tween.dispose();
    });
    if (rotateWhileZooming) new Tween(0, player.direction, 2, 'easeInOutQuad', (v) => {
        cameraDirection = v;
    });
}

// Convert world coordinates to canvas coordinates
// TODO: If this function serves only as a pixel-per-meter conversion, it should probably be deleted
function worldToCanvas(vector) {
    return { // FIXME: Not great as it will create a new object every time
        x: vector.x * config.pixelsPerMeter,
        y: vector.y * config.pixelsPerMeter,
    };
}